/**
 * Feature flags for tab visibility. Managed in Settings (admin tab) and stored in DB.
 * Each key corresponds to a top-level app tab/route. Settings is not included — always available.
 */
export const FEATURE_FLAG_IDS = [
  'dashboard',
  'analysis',
  'scheduledAnalyses',
  'results',
  'cop',
  'pathfinder',
  'linkAnalysis',
  'patternOfLife',
  'itinerary',
  'dashboardBuilder',
  'rfDashboard',
  'adtech',
  'overwatch',
  'intel',
] as const;

export type FeatureFlagId = (typeof FEATURE_FLAG_IDS)[number];

/** Map flag id -> route path (single source of truth for nav + routing).
 *  rfDashboard ("RF Signal Module") gates the dedicated DFP / Force Protection page. */
export const FEATURE_FLAG_PATHS: Partial<Record<FeatureFlagId, string>> = {
  dashboard: '/dashboard',
  analysis: '/analysis',
  scheduledAnalyses: '/scheduled-analyses',
  results: '/results',
  cop: '/cop',
  pathfinder: '/pathfinder',
  intel: '/intel',
  linkAnalysis: '/link-analysis',
  patternOfLife: '/pattern-of-life',
  itinerary: '/itinerary',
  dashboardBuilder: '/content',
  rfDashboard: '/force-protection',
};

/** Extra route prefixes that ride along with a flag's primary path. */
export const FEATURE_FLAG_PATH_ALIASES: Partial<Record<FeatureFlagId, string[]>> = {
  intel: ['/missions'],
};

/** Display labels for admin UI */
export const FEATURE_FLAG_LABELS: Record<FeatureFlagId, string> = {
  dashboard: 'Command Center',
  analysis: 'Analysis',
  scheduledAnalyses: 'Scheduled Analysis',
  results: 'Results',
  cop: 'COP Dashboard',
  pathfinder: 'Pathfinder',
  linkAnalysis: 'Link Analysis',
  patternOfLife: 'Pattern of Life',
  itinerary: 'Itinerary',
  dashboardBuilder: 'Dashboard Builder',
  rfDashboard: 'RF Signal Module',
  adtech: 'AdTech',
  overwatch: 'OSINT',
  intel: 'Intel Packet',
};

export type FeatureFlagsState = Record<FeatureFlagId, boolean>;

export const DEFAULT_FEATURE_FLAGS: FeatureFlagsState = Object.fromEntries(
  FEATURE_FLAG_IDS.map((id) => [id, true])
) as FeatureFlagsState;

export type NavPlacement = 'left' | 'top';

export const DEFAULT_NAV_PLACEMENT: NavPlacement = 'left';

/** Top/bottom classification strip (Settings → Modules, admin). */
export type ClassificationBannerMode = 'commercial' | 'unclassified' | 'cui';

export const DEFAULT_CLASSIFICATION_BANNER_MODE: ClassificationBannerMode = 'cui';

export const DEFAULT_PARTICIPANT_LABEL_SINGULAR = 'Entity';
export const DEFAULT_PARTICIPANT_LABEL_PLURAL = 'Entities';

/** Training/course grouping — UI label (default Domain / Domains). */
export const DEFAULT_COURSE_LABEL_SINGULAR = 'Domain';
export const DEFAULT_COURSE_LABEL_PLURAL = 'Domains';

/**
 * Select/API sentinel for “no course filter” (must stay stable for queries & localStorage).
 * Use {@link useCourseLabels} `allLabel` for display text (e.g. “All Domains”).
 */
export const ALL_COURSES_SELECT_VALUE = 'All Courses';

/** Order for “first available tab” when a gated route is disabled (avoids redirect loops). */
export const FEATURE_FLAG_FALLBACK_ORDER: FeatureFlagId[] = [
  'dashboard',
  'analysis',
  'results',
  'scheduledAnalyses',
  'cop',
  'pathfinder',
  'intel',
  'linkAnalysis',
  'patternOfLife',
  'itinerary',
  'dashboardBuilder',
  'rfDashboard',
];
